import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useManageCandidateSelectionContext } from "../../../ManageCandidates/State/ManageCandidateContext";
import { CandidateEmailType, CandidatePhoneNumberType } from "../../Enums/CandidateEnums";
import { useCreateCandidate } from "../../Queries/CandidateQueries";
import { useGroupsQuery } from "../../Queries/GroupQueries";
import { validateEmail } from "../../Scripts/emailValidator";
import { useNotification } from "../../State/Hooks/notificationHooks";
import { IAddCandidate } from "../../Typings/Candidate";
import { IGroup } from "../../Typings/Groups";
import { LocationField } from "../Forms/LocationField";
import { RequiredIndicator } from "../Forms/formHelpers";
import Modal from "../Modal/Modal";

export default function AddCandidateModal() {
  const emptyCandidate = {
    company: "",
    title: "",
    email: {
      emailAddress: "",
      emailAddressType: CandidateEmailType.Personal,
    },
    phone: {
      phoneNumber: "",
      phoneNumberType: CandidatePhoneNumberType.Mobile,
    },
    firstName: "",
    lastName: "",
    groupId: "none",
    location: "",
    userId: "",
  };
  const {
    getters: { isAddCandidateOpen },
    setters: { setAddCandidateOpen },
  } = useManageCandidateSelectionContext();
  const [candidate, setCandidate] = useState<IAddCandidate>(emptyCandidate);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const { mutate: createCandidate } = useCreateCandidate();
  const { data: groups } = useGroupsQuery();
  const { displaySuccess, displayError } = useNotification();

  const closeModal = () => {
    setCandidate(emptyCandidate);
    setAddCandidateOpen(false);
  };

  const saveCandidate = () => {
    setIsSaving(true);
    const error = validateCandidate();

    if (error === "") {
      createCandidate(
        {
          ...candidate,
          groupId: candidate.groupId === "none" ? undefined : candidate.groupId,
        },
        {
          onSuccess: () => {
            displaySuccess("Candidate saved.");
            closeModal();
            setIsSaving(false);
          },
          onError: () => {
            displayError("There was an error saving the candidate.");
            setIsSaving(false);
          },
        },
      );
    } else {
      displayError(error);
      setIsSaving(false);
    }
  };

  const validateCandidate = () => {
    if (!candidate.firstName || !candidate.lastName) {
      return "First and last name are required.";
    }

    if (!candidate.phone.phoneNumber.trim() && !candidate.email.emailAddress.trim()) {
      return "At least one valid email or phone number is required.";
    }

    if (candidate.phone.phoneNumber.trim() && !validatePhone(candidate.phone.phoneNumber)) {
      return "Phone number is invalid.";
    }

    if (candidate.email.emailAddress.trim() && !validateEmail(candidate.email.emailAddress)) {
      return "Email address is invalid.";
    }

    return "";
  };

  return (
    <Modal
      content={
        <Box minHeight={400}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12} md={6} lg={6}>
              <FormLabel htmlFor="candidate-first-name" sx={{ fontWeight: "bold", mb: 0.5 }}>
                First Name
                <RequiredIndicator />
              </FormLabel>
              <TextField
                id="candidate-first-name"
                data-test="candidate-first-name-field"
                variant="outlined"
                fullWidth
                required
                placeholder="John"
                onChange={(ev) => {
                  setCandidate({
                    ...candidate,
                    firstName: ev.target.value,
                  });
                }}
              />
              {!candidate.firstName && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }} id={"candidate-first-name-error"}>
                  {"First name is required."}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6}>
              <FormLabel htmlFor="candidate-last-name" sx={{ fontWeight: "bold", mb: 0.5 }}>
                Last Name
                <RequiredIndicator />
              </FormLabel>
              <TextField
                id="candidate-last-name"
                data-test="candidate-last-name-field"
                variant="outlined"
                fullWidth
                placeholder="Doe"
                onChange={(ev) => {
                  setCandidate({
                    ...candidate,
                    lastName: ev.target.value,
                  });
                }}
              />
              {!candidate.lastName && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }} id={"candidate-last-name-error"}>
                  {"Last name is required."}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6}>
              <FormLabel htmlFor="candidate-title" sx={{ fontWeight: "bold", mb: 0.5 }}>
                Title
              </FormLabel>
              <TextField
                id="candidate-title"
                variant="outlined"
                fullWidth
                onChange={(ev) => {
                  setCandidate({
                    ...candidate,
                    title: ev.target.value,
                  });
                }}
                data-test="candidate-title-field"
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6}>
              <FormLabel htmlFor="candidate-company" sx={{ fontWeight: "bold", mb: 0.5 }}>
                Company
              </FormLabel>
              <TextField
                id="candidate-company"
                variant="outlined"
                fullWidth
                onChange={(ev) => {
                  setCandidate({
                    ...candidate,
                    company: ev.target.value,
                  });
                }}
                data-test="candidate-company-field"
              />
            </Grid>
            <Grid item xs={12}>
              <FormLabel htmlFor="candidate-location" sx={{ fontWeight: "bold", mb: 0.5 }}>
                Location
              </FormLabel>
              <LocationField
                fieldName="candidate-location"
                id="candidate-location"
                onChange={(loc) => () => {
                  setCandidate({
                    ...candidate,
                    location: loc,
                  });
                }}
                onInputChange={(loc) => {
                  setCandidate({
                    ...candidate,
                    location: loc,
                  });
                }}
                value={candidate.location}
              />
            </Grid>
            <Grid item container xs={12} spacing={2}>
              <Grid item xs={8}>
                <FormLabel htmlFor="candidate-phone" sx={{ fontWeight: "bold", mb: 0.5 }}>
                  Phone
                </FormLabel>
                <TextField
                  id="candidate-phone"
                  variant="outlined"
                  data-test="candidate-phone-field"
                  fullWidth
                  onChange={(ev) => {
                    setCandidate({
                      ...candidate,
                      phone: {
                        ...candidate.phone,
                        phoneNumber: ev.target.value,
                      },
                    });
                  }}
                  placeholder="1-555-555-2029"
                />
                {candidate.phone.phoneNumber && !validatePhone(candidate.phone.phoneNumber) && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1 }}
                    id={"candidate-first-name-error"}
                  >
                    {"Invalid phone number."}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <FormLabel htmlFor="candidate-phone-type" sx={{ fontWeight: "bold", mb: 0.5 }}>
                    Phone Type
                  </FormLabel>
                  <Select
                    id="candidate-phone-type"
                    value={candidate.phone.phoneNumberType}
                    onChange={(ev) => {
                      setCandidate({
                        ...candidate,
                        phone: {
                          ...candidate.phone,
                          phoneNumberType: ev.target.value,
                        },
                      });
                    }}
                  >
                    {printEnumValues(CandidatePhoneNumberType)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid item container xs={12} spacing={2}>
              <Grid item xs={8}>
                <FormLabel htmlFor="candidate-email" sx={{ fontWeight: "bold", mb: 0.5 }}>
                  Email Address
                </FormLabel>
                <TextField
                  id="candidate-email"
                  variant="outlined"
                  fullWidth
                  placeholder="example@domain.com"
                  data-test="candidate-email-field"
                  onChange={(ev) => {
                    setCandidate({
                      ...candidate,
                      email: {
                        ...candidate.email,
                        emailAddress: ev.target.value,
                      },
                    });
                  }}
                />
                {candidate.email.emailAddress && !validateEmail(candidate.email.emailAddress) && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1 }}
                    id={"candidate-first-name-error"}
                  >
                    {"Invalid email address."}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <FormLabel htmlFor="candidate-email-type" sx={{ fontWeight: "bold", mb: 0.5 }}>
                    Email Type
                  </FormLabel>
                  <Select
                    id="candidate-email-type"
                    value={candidate.email?.emailAddressType}
                    onChange={(ev) => {
                      setCandidate({
                        ...candidate,
                        email: {
                          ...candidate.email,
                          emailAddressType: ev.target.value,
                        },
                      });
                    }}
                  >
                    {printEnumValues(CandidateEmailType)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <FormLabel htmlFor="candidate-group" sx={{ fontWeight: "bold", mb: 0.5 }}>
                  Group
                </FormLabel>
                <Select
                  id="candidate-group"
                  data-test="candidate-group-select"
                  value={candidate.groupId}
                  onChange={(ev) => {
                    setCandidate({
                      ...candidate,
                      groupId: ev.target.value,
                    });
                  }}
                >
                  <MenuItem key="none" value="none">
                    None
                  </MenuItem>
                  {printGroupOptions(groups?.items ?? [])}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      }
      dataTestId="add-candidate"
      footerContent={
        <Box sx={{ width: "100%" }}>
          <Divider sx={{ width: "100%", marginBottom: 2 }} />
          <Stack direction={"row"} justifyContent={"left"} spacing={2} sx={{ marginBottom: 1 }}>
            <Box sx={{ height: 35, paddingLeft: 2 }}>
              <Typography variant="caption" sx={{ lineHeight: 0.5, fontSize: 12 }}>
                By adding this candidate, you confirm that you have obtained the necessary consent to contact
                them as required by applicable laws and regulations.
              </Typography>
            </Box>
            {isSaving && <CircularProgress sx={{ mt: 1, mr: 1 }} size={20} />}
            <Button
              data-test="candidate-save-button"
              variant="contained"
              onClick={() => saveCandidate()}
              disabled={isSaving}
            >
              Save
            </Button>
            <Button data-test="candidate-close-button" color="info" variant="contained" onClick={closeModal}>
              Cancel
            </Button>
          </Stack>
        </Box>
      }
      handleCloseModal={closeModal}
      isOpen={isAddCandidateOpen}
      title="Add Candidate"
      size="md"
    ></Modal>
  );
}

const printGroupOptions = (groups: IGroup[]) => {
  return groups
    .sort((a, b) => {
      if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
      if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;

      return 1;
    })
    .filter((g) => !g.isDynamic)
    .map((c) => (
      <MenuItem key={`group-${c.id}`} value={c.id}>
        {c.name} ({c.memberCount})
      </MenuItem>
    ));
};

const printEnumValues = (e: any) => {
  const items = [];
  for (let i = 0; i < Object.keys(e).length; i++) {
    if (typeof e[i] === "string") {
      items.push(
        <MenuItem key={`option-${i}`} value={i}>
          {e[i]}
        </MenuItem>,
      );
    }
  }
  return items;
};

const validatePhone = (phone: string) => {
  return phone && phone.replace(/\D/g, "").length >= 10;
};
